import { useState } from 'react';
import { supabase } from '../supabaseClient';

export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [error, setError] = useState(null);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const handleAuth = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isForgotPassword) {
        const { data: waitlistEntry, error: waitlistError } = await supabase
          .from('waitlist')
          .select('approved, user_id')
          .eq('email', email)
          .maybeSingle();

        if (waitlistError) {
          throw new Error('Error checking user status. Please try again.');
        }
        if (!waitlistEntry) {
          throw new Error('User not found. Join the waitlist first.');
        }
        if (!waitlistEntry.approved) {
          throw new Error('Your account is not approved yet for beta access.');
        }

        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`
        });
        if (resetError) throw resetError;
        setResetEmailSent(true);
      } else if (isSignUp) {
        const { error: signUpError } = await supabase.auth.signUp({ email, password });
        if (signUpError) throw signUpError;
        setSignUpSuccess(true);
      } else {
        const { data: waitlistEntry, error: waitlistError } = await supabase
          .from('waitlist')
          .select('approved, user_id')
          .eq('email', email)
          .maybeSingle();

        if (waitlistError) {
          throw new Error('Error checking user status. Please try again.');
        }
        if (!waitlistEntry) {
          throw new Error('User not found. Join the waitlist first.');
        }
        if (!waitlistEntry.approved) {
          throw new Error('Your account is not approved yet for beta access.');
        }

        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) {
          if (signInError.message.includes('Invalid login credentials')) {
            throw new Error('Invalid password. Use Forgot Password if needed.');
          }
          throw signInError;
        }

        window.location.href = '/dashboard';
        return;
      }
    } catch (authError) {
      if (authError.message.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please try again.');
      } else {
        setError(authError.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <span className="brand-icon">C</span>
          <div>
            <h1>CrypnAI</h1>
            <p>Crypto analysis built from market data</p>
          </div>
        </div>

        <h2>
          {resetEmailSent
            ? 'Check your email'
            : signUpSuccess
            ? 'Confirm your email'
            : isForgotPassword
            ? 'Reset your password'
            : isSignUp
            ? 'Create account'
            : 'Sign in'}
        </h2>

        {!signUpSuccess && !resetEmailSent && isForgotPassword && (
          <p className="auth-meta">
            Remembered your password?{' '}
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setIsForgotPassword(false);
                setError(null);
              }}
            >
              Sign in
            </button>
          </p>
        )}

        {error && <div className="error-banner"><p>{error}</p></div>}

        {!signUpSuccess && !resetEmailSent ? (
          <form className="auth-form" onSubmit={handleAuth}>
            <input
              id="email-address"
              name="email"
              type="email"
              required
              className="input-field"
              placeholder="Email address"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />

            {!isForgotPassword && (
              <input
                id="password"
                name="password"
                type="password"
                required
                className="input-field"
                placeholder="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}

            {!isSignUp && !isForgotPassword && (
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setIsForgotPassword(true);
                  setError(null);
                }}
              >
                Forgot your password?
              </button>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading
                ? 'Processing...'
                : isForgotPassword
                ? 'Send reset email'
                : isSignUp
                ? 'Sign up'
                : 'Sign in'}
            </button>

            {!isForgotPassword && (
              <p className="auth-meta">
                {isSignUp ? 'Already have an account?' : 'Need an account?'}{' '}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => {
                    setIsSignUp((prev) => !prev);
                    setError(null);
                  }}
                >
                  {isSignUp ? 'Sign in' : 'Sign up'}
                </button>
              </p>
            )}
          </form>
        ) : (
          <div className="success-box">
            <p>
              {resetEmailSent
                ? 'Password reset email sent. Open the link in your inbox to continue.'
                : 'Check your email to confirm your account.'}
            </p>
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => {
                setIsSignUp(false);
                setSignUpSuccess(false);
                setResetEmailSent(false);
                setIsForgotPassword(false);
                setError(null);
              }}
            >
              Back to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
